<template>
    <div class="l-learning-admin-element-edit-quiz-form">
        <div class="body">
            <l-input-text
                ref="input-name"
                v-model="element.name"
                type="text"
                :label="''"
                :placeholder="$t(`learning.admin.element.details.placeholder.name`)"
                data-test-id="element-edit-name"
            ></l-input-text>
            <l-input-text
                ref="input-description"
                v-model="element.description"
                type="textarea"
                :optional="true"
                :show-text-format-hint="true"
                :label="''"
                :placeholder="$t(`learning.admin.element.details.placeholder.description`)"
                data-test-id="element-edit-description"
            ></l-input-text>

            <h4 class="color-dark">
                <strong>{{ $t(`learning.admin.element.details.label.questions`) }}</strong>
            </h4>
            <hr />
            <div
                v-for="(question, questionIndex) in element.questions"
                :key="`question-${questionIndex}`"
                class="question"
            >
                <div>
                    <l-input-text
                        ref="input-questions"
                        v-model="question.title"
                        type="text"
                        :label="''"
                        :placeholder="$t('learning.admin.element.details.placeholder.question')"
                        :data-test-id="`element-edit-question-${questionIndex}-name`"
                    />
                    <div class="option-delete">
                        <i
                            v-if="element.questions.length > 1"
                            class="fa fa-trash fake-link clickable"
                            tabindex="0"
                            :data-test-id="`element-edit-question-${questionIndex}-delete`"
                            @click="() => deleteQuestion(questionIndex)"
                            @keyup.enter="deleteQuestion(questionIndex)"
                        ></i>
                        <p v-else>&nbsp;</p>
                    </div>
                </div>
                <l-input-text
                    v-model="question.description"
                    type="text"
                    :optional="true"
                    :label="''"
                    :placeholder="$t('learning.admin.element.details.placeholder.description')"
                    :data-test-id="`element-edit-question-${questionIndex}-description`"
                />
                <div
                    v-for="(option, optionIndex) in question.options"
                    :key="`question-${questionIndex}-option-${optionIndex}`"
                    class="option"
                >
                    <!-- Label added for accessibility -->
                    <label
                        :for="`option-correct-${questionIndex}-${optionIndex}-check`"
                        style="display:none;"
                    >
                        Correct
                    </label>

                    <input
                        :id="`option-correct-${questionIndex}-${optionIndex}-check`"
                        v-model="option.correct"
                        v-b-popover.hover.left="{
                            content: $t('learning.admin.element.details.tooltip.markCorrect'),
                        }"
                        type="checkbox"
                        :data-test-id="
                            `element-edit-question-${questionIndex}-option-${optionIndex}-correct`
                        "
                        @input="toggleCorrectOption(questionIndex, optionIndex)"
                    />
                    <l-input-text
                        ref="input-answers"
                        v-model="option.title"
                        type="text"
                        :label="''"
                        :placeholder="$t(`learning.admin.element.details.placeholder.option`)"
                        :data-test-id="
                            `element-edit-question-${questionIndex}-option-${optionIndex}-name`
                        "
                    />
                    <div class="option-delete">
                        <i
                            v-if="question.options.length > 1"
                            class="fa fa-trash fake-link clickable"
                            tabindex="0"
                            :data-test-id="
                                `element-edit-question-${questionIndex}-option-${optionIndex}-delete`
                            "
                            @click="() => deleteOption(questionIndex, optionIndex)"
                            @keyup.enter="deleteOption(questionIndex, optionIndex)"
                        ></i>
                        <p v-else>&nbsp;</p>
                    </div>
                </div>
                <div class="text-right">
                    <div class="option-delete">
                        <i
                            v-b-popover.hover.left="{
                                content: $t('learning.admin.element.details.btn.addOption'),
                            }"
                            class="fa fa-plus fake-link clickable"
                            tabindex="0"
                            :data-test-id="`element-edit-question-${questionIndex}-option-add`"
                            @click="() => addNewOption(questionIndex)"
                            @keyup.enter="addNewOption(questionIndex)"
                        ></i>
                    </div>
                </div>
                <hr />
            </div>
            <div>
                <button
                    class="btn btn-primary btn-framed btn-framed-primary question-add"
                    :data-test-id="`element-edit-question-add`"
                    @click="addNewQuestion"
                >
                    <i class="fa fa-plus"></i>
                    {{ $t("learning.admin.element.details.btn.addQuestion") }}
                </button>
            </div>
        </div>

        <br />

        <div class="footer">
            <div
                v-b-popover.hover.topleft="{
                    content: $t('learning.admin.element.details.tooltip.deleteDisabled'),
                    disabled:
                        !showDeleteBtn || !(element.usedInPaths && element.usedInPaths.length),
                }"
            >
                <button
                    v-show="showDeleteBtn"
                    class="btn btn-default btn-raised"
                    :disabled="
                        disableSubmit || Boolean(element.usedInPaths && element.usedInPaths.length)
                    "
                    data-test-id="element-delete"
                    @click="deleteElement"
                >
                    <i class="fa fa-remove"></i>
                    {{ $t("learning.admin.element.details.btn.delete") }}
                </button>
            </div>
            <div>
                <button class="btn" data-test-id="element-cancel" @click="cancel">
                    {{ $t("learning.admin.element.details.btn.back") }}
                </button>
                <button
                    class="btn btn-primary btn-raised"
                    :disabled="disableSubmit"
                    data-test-id="element-submit"
                    @click="submit"
                >
                    {{ $t("learning.admin.element.details.btn.save") }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import LInputText from "@/components/LInputText";

export default {
    components: {
        LInputText,
    },
    props: {
        initialData: {
            type: Object,
            required: true,
        },
        showDeleteBtn: {
            type: Boolean,
            default: true,
        },
        disableSubmit: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            element: { ...this.initialData },
        };
    },
    created() {
        if (!Array.isArray(this.element.questions)) {
            this.element.questions = [];
        }

        if (!this.element.questions.length) {
            this.element.questions.push({
                type: "choice", // FIXME - Make it dynamic when adding support to other types of questions (like free text)
                title: "",
                description: "",
                options: [{}, {}, {}], // Initialize questions with 3 empty options
            });
        }
    },
    methods: {
        addNewQuestion() {
            if (!Array.isArray(this.element.questions)) {
                this.element.questions = [];
            }
            this.element.questions.push({
                type: "choice",
                title: "",
                description: "",
                options: [{}, {}, {}],
            });
        },
        deleteQuestion(index) {
            if (0 <= index && index < this.element.questions.length) {
                this.element.questions.splice(index, 1);
            }
        },
        addNewOption(questionIndex) {
            this.element.questions[questionIndex].options.push({});
        },
        deleteOption(questionIndex, index) {
            if (
                this.element.questions[questionIndex] &&
                this.element.questions[questionIndex].options.length > 1
            ) {
                if (0 <= index && index < this.element.questions[questionIndex].options.length) {
                    this.element.questions[questionIndex].options.splice(index, 1);
                }
            }
        },
        toggleCorrectOption(questionIndex, optionIndex) {
            // Only one answer should be selected at once so force all other options to `false`
            //
            // FIXME - This should probably be a radio input
            //
            this.element.questions[questionIndex].options.forEach((option, index) => {
                if (index === optionIndex && !option.correct) {
                    this.$set(option, "correct", true);
                    return;
                }
                this.$set(option, "correct", false);
            });
        },

        isValid() {
            const inputs = Object.keys(this.$refs)
                .map(key => (key.startsWith("input-") ? this.$refs[key] : null))
                .filter(Boolean)
                .flat(); // We have nested refs!

            return (
                inputs.reduce((a, c) => c.validate() && a, true) && this.element.questions.length
            );
        },

        deleteElement() {
            this.$emit("deleteElement");
        },
        cancel() {
            this.$emit("cancel");
        },
        submit() {
            if (this.isValid()) {
                this.$emit("submit", this.element);
            }
        },
    },
};
</script>

<style scoped>
.l-learning-admin-element-edit-quiz-form .question >>> .form-group.label-floating {
    width: calc(100% - 40px);
    display: inline-block;
    margin-right: 20px;
}

.option {
    padding-left: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.option .form-group.label-floating {
    flex-grow: 1;
    margin-right: 20px;
}
.option input[type="checkbox"] {
    margin-right: 24px;
    margin-bottom: 40px;
    position: relative;
    cursor: pointer;
}
.option input[type="checkbox"]:before {
    content: "";
    display: block;
    position: absolute;
    width: 20px;
    height: 20px;
    top: 0;
    left: 0;
    border: 2px solid #9e9e9e;
    border-radius: 3px;
    background-color: white;
}
.option input[type="checkbox"]:checked:after {
    content: "";
    display: block;
    width: 6px;
    height: 12px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    position: absolute;
    top: 2px;
    left: 7px;
}
.option input[type="checkbox"]:checked::before {
    border-color: var(--color-primary);
    background-color: var(--color-primary);
}

.option-delete {
    margin-left: 8px;
    margin-bottom: 25px;
    width: 12px;
    display: inline-block;
}

.btn.option-add {
    margin-right: 35px;
}

/* Footer */
.footer {
    display: flex;
}
.footer div:first-of-type {
    flex-grow: 1;
}
</style>
